import useAppStore from "@/context/store";
import { useMemo } from "react";

// ============================================================
// 类型定义
// ============================================================

/**
 * 堆栈中的采集项
 *
 * 基于 IPin 的部分字段，加上堆栈特有字段
 *
 * 支持两种数据模式：
 * 1. 扁平模式：pin 数据直接展开在根层级（最常见）
 * 2. 嵌套模式：完整 pin 数据在 pin 字段中
 */
export interface PinStackItem extends Partial<IPin> {
  /** 采集 ID 别名（由 pushPinToStack 添加） */
  pinId?: number | string;

  /** 完整采集数据（嵌套模式时使用） */
  pin?: IPin;

  /** 渐进式加载：缓存的图片地址 */
  currentSrc?: string;

  /** 搜索词（用于埋点和推荐） */
  searchWord?: string;

  /** 是否允许空数据（后续异步加载） */
  allowEmpty?: boolean;

  /** 入栈时间戳（由 pushPinToStack 自动添加） */
  _timestamp?: number;
}

/**
 * 堆栈核心上下文
 */
export interface PinStackCoreContext {
  /** 原始堆栈数据 */
  pinStack: PinStackItem[];
  /** 堆栈深度 */
  depth: number;
  /** 当前采集（栈顶） */
  current: PinStackItem | null;
  /** 是否有浏览历史（深度 > 1） */
  hasHistory: boolean;
}

/**
 * 浏览历史数据
 */
export interface BrowsingHistory {
  /** 历史采集列表（最近访问的在最前面） */
  items: PinStackItem[];
  /** file_id 列表（最近访问的在最前面） */
  fileIds: string[];
  /** pin_id 列表（最近访问的在最前面） */
  pinIds: string[];
  /** 埋点用字符串：file_ids（逗号分隔，最近访问的在最前面） */
  fileIdsString: string;
  /** 历史记录数量 */
  count: number;
}

/**
 * 完整的堆栈上下文
 */
export interface PinStackContext extends PinStackCoreContext {
  /** 浏览历史 */
  history: BrowsingHistory;
  /** 便捷别名：file_id 列表 */
  fileIds: string[];
  /** 便捷别名：pin_id 列表 */
  pinIds: string[];
  /** 便捷别名：埋点用字符串 */
  sourceFileIds: string;
  /** 便捷别名：历史采集列表（兼容旧代码） */
  previousPins: PinStackItem[];
  /** 便捷别名：堆栈深度（兼容旧代码） */
  stackDepth: number;
  /** 便捷别名：当前采集（兼容旧代码） */
  currentPin: PinStackItem | null;
  /** 便捷别名：是否有浏览历史（兼容旧代码） */
  hasBrowsingHistory: boolean;
}

// ============================================================
// 工具函数
// ============================================================

/**
 * 从堆栈项中提取 file_id
 * 兼容扁平模式和嵌套模式
 */
function getFileId(item: PinStackItem): string | null {
  const fileId = item.file_id ?? item.pin?.file_id ?? item.file?.id;
  return fileId != null ? String(fileId) : null;
}

/**
 * 从堆栈项中提取 pin_id
 * 兼容 pin_id、pinId 和嵌套 pin.pin_id
 */
function getPinId(item: PinStackItem): string | null {
  const pinId = item.pin_id ?? item.pinId ?? item.pin?.pin_id;
  return pinId != null ? String(pinId) : null;
}

/**
 * 批量提取 ID
 * @param items 堆栈项列表
 * @param extractor 提取函数
 */
function extractIds(
  items: PinStackItem[],
  extractor: (item: PinStackItem) => string | null
): string[] {
  const result: string[] = [];
  for (const item of items) {
    const id = extractor(item);
    if (id !== null) {
      result.push(id);
    }
  }
  return result;
}

// ============================================================
// Hooks
// ============================================================

/**
 * 获取堆栈核心数据
 * 轻量级 Hook，适用于只需要基础状态的场景
 */
export function usePinStackCore(): PinStackCoreContext {
  const pinStack = useAppStore((state) => state.pinStack) ?? [];

  const depth = pinStack.length;
  const current = depth > 0 ? pinStack[depth - 1] : null;
  const hasHistory = depth > 1;

  return { pinStack, depth, current, hasHistory };
}

/**
 * 获取浏览历史
 *
 * ⚠️ 返回数据顺序：最近访问的在最前面（符合后端期望）
 *
 * @param options.includeCurrent 是否包含当前采集，默认 false
 * @param options.maxCount 最大返回数量，默认 10
 */
export function useBrowsingHistory(
  options: {
    includeCurrent?: boolean;
    maxCount?: number;
  } = {}
): BrowsingHistory {
  const { includeCurrent = false, maxCount = 10 } = options;
  const { pinStack, depth } = usePinStackCore();

  // 计算历史记录列表
  const items = useMemo(() => {
    if (depth === 0 || (depth === 1 && !includeCurrent)) {
      return [];
    }

    // 1. 根据 includeCurrent 确定基础列表
    const base = includeCurrent ? pinStack : pinStack.slice(0, -1);

    // 2. 限制数量（从栈底开始取，保证是最早的 maxCount 个历史）
    const limited =
      base.length > maxCount ? base.slice(base.length - maxCount) : base;

    // 3. 反转：最近访问的在最前面
    const result: PinStackItem[] = [];
    for (let i = limited.length - 1; i >= 0; i--) {
      result.push(limited[i]);
    }

    return result;
  }, [pinStack, depth, includeCurrent, maxCount]);

  // 提取 file_ids
  const fileIds = useMemo(() => extractIds(items, getFileId), [items]);

  // 提取 pin_ids
  const pinIds = useMemo(() => extractIds(items, getPinId), [items]);

  // 生成埋点字符串
  const fileIdsString = useMemo(() => fileIds.join(","), [fileIds]);

  return {
    items,
    fileIds,
    pinIds,
    fileIdsString,
    count: items.length,
  };
}

/**
 * 完整的堆栈上下文 Hook
 *
 * 组合 usePinStackCore 和 useBrowsingHistory，
 * 同时提供兼容旧代码的别名字段
 *
 * @param options.includeCurrent 是否包含当前采集，默认 false
 * @param options.maxCount 最大返回数量，默认 10
 */
export function usePinStackContext(
  options: {
    includeCurrent?: boolean;
    maxCount?: number;
  } = {}
): PinStackContext {
  const core = usePinStackCore();
  const history = useBrowsingHistory(options);

  return {
    // 核心数据
    ...core,
    history,

    // 便捷访问（最常用的字段直接暴露）
    fileIds: history.fileIds,
    pinIds: history.pinIds,
    sourceFileIds: history.fileIdsString,

    // 兼容旧代码的别名
    previousPins: history.items,
    stackDepth: core.depth,
    currentPin: core.current,
    hasBrowsingHistory: core.hasHistory,
  };
}

// ============================================================
// 导出工具函数（供外部使用）
// ============================================================

export { extractIds, getFileId, getPinId };
